import clsx from 'clsx';
import type { RefObject } from 'react';

import type * as api from '@gitbook/api-client';
import type {
    RawEditAnalyticsEvent,
    RawQuickstartAnalyticsEventAction,
} from '@gitbook/app-analytics/client';
import { ResponsiveButtonsRow, type ResponsiveRowButton } from '@gitbook/react-uikit';
import type { Block, Change } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { ImportInSpaceDialog, TemplateDialog } from '../../../../Components';
import { introBlocks, multiSyntaxCodeBlocks, stepperBlocks, twoColumnsBlocks } from './content';

type InsertBlockCallback = (change: Change, block: Block | Array<Block>) => void;

/**
 * Blank state for a space to prompt to use a template or import some content
 */
export function SpaceQuickstart(props: {
    editorRef: RefObject<EditorContainer>;
    logAnalyticsEvent: (event: RawEditAnalyticsEvent) => void;
    className?: string;
    space: api.Space;
    isSpaceEmpty: boolean;
    pageId: string;
}) {
    const { editorRef, logAnalyticsEvent, space, className, isSpaceEmpty, pageId } = props;

    // Insert a block into the editor.
    const insertBlock = useEventCallback(
        (block: Block | Array<Block>, callback?: InsertBlockCallback) => {
            editorRef.current?.change((change) => {
                change.collapseToStartOf(change.value.document).focus();

                let next: Change = change;
                if (Array.isArray(block)) {
                    const [first, ...rest] = block;

                    // Insert first block using insertOrReplaceBlock
                    next = change.insertOrReplaceBlock(first);

                    // Insert remaining blocks after each other
                    let previousKey = first.key;
                    rest.forEach((b) => {
                        const parent = next.value.document.getParent(previousKey);
                        if (parent) {
                            change.collapseTo(parent.key).focus();
                            const nodeIndex = parent.getChildIndex(previousKey);
                            if (nodeIndex >= 0) {
                                next = next.insertNodeByKey(parent.key, nodeIndex + 1, b);
                                previousKey = b.key;
                            }
                        } else {
                            next = next.insertOrReplaceBlock(b);
                            previousKey = b.key;
                        }
                    });
                } else {
                    next = change.insertOrReplaceBlock(block);
                }

                callback?.(change, block);
            });
        }
    );

    const logEvent = useEventCallback((action: RawQuickstartAnalyticsEventAction) => {
        logAnalyticsEvent({ type: 'edit_page_document_quickstart_action', action });
    });

    const [buttons, elements] = useSpaceQuickstartActions({
        insertBlock,
        space,
        logEvent,
        isSpaceEmpty,
        pageId,
    });

    return (
        <>
            <ResponsiveButtonsRow
                kind="secondary"
                buttons={buttons}
                className={clsx('gap-x-2', className)}
            />
            {elements}
        </>
    );
}

function useSpaceQuickstartActions(props: {
    insertBlock: (block: Block | Array<Block>, callback?: InsertBlockCallback) => void;
    space: api.Space;
    logEvent: (action: RawQuickstartAnalyticsEventAction) => void;
    isSpaceEmpty: boolean;
    pageId: string;
}): [ResponsiveRowButton[], React.ReactNode] {
    const { insertBlock, space, logEvent, isSpaceEmpty, pageId } = props;

    const templateDialog = useDialogTriggerState();
    const importDialog = useDialogTriggerState();

    const options = [
        {
            key: 'intro',
            label: 'Intro',
            leadingIcon: Icon.Heading1,
            onPress: () => {
                logEvent('intro');
                insertBlock(introBlocks);
            },
        },
        {
            key: 'two-columns',
            label: 'Two columns',
            leadingIcon: Icon.Columns,
            onPress: () => {
                logEvent('two-columns');
                insertBlock(twoColumnsBlocks);
            },
        },
        {
            key: 'multi-syntax-code',
            label: 'Multi-syntax code',
            leadingIcon: Icon.Code,
            onPress: () => {
                logEvent('multi-syntax-code');
                insertBlock(multiSyntaxCodeBlocks);
            },
        },
        {
            key: 'step-by-step-guide',
            label: 'Step-by-step guide',
            leadingIcon: Icon.Stepper,
            onPress: () => {
                logEvent('step-by-step-guide');
                insertBlock(stepperBlocks);
            },
        },
        isSpaceEmpty
            ? {
                  key: 'use-template',
                  label: 'Use template',
                  inPalette: true,
                  leadingIcon: Icon.Template,
                  onPress: () => {
                      templateDialog.open();
                  },
              }
            : null,
        {
            key: 'import-content',
            label: 'Import content',
            inPalette: true,
            leadingIcon: Icon.Download,
            onPress: () => {
                importDialog.open();
            },
        },
    ].filter(filterOutNullable);

    return [
        options,
        <>
            <DialogRoot {...templateDialog.rootProps} key="template-dialog">
                <TemplateDialog
                    organizationId={space.organization}
                    space={space}
                    onComplete={() => {
                        logEvent('use-template');
                        templateDialog.close();
                    }}
                />
            </DialogRoot>
            <DialogRoot {...importDialog.rootProps} key="import-dialog">
                <ImportInSpaceDialog
                    page={pageId}
                    onStart={() => {
                        logEvent('import-content');
                    }}
                />
            </DialogRoot>
        </>,
    ];
}
